import React from 'react';
import {
  DragAndDrop
} from '@progress/kendo-react-common';
import { Component } from '../../../dispatcher/modules';
import {
  CONDITIONAL_FILTER_FILTERS,
  CONDITIONAL_FILTER_PROPS,
  CONDITIONAL_FILTER_FILTERS_STATE
} from './Types';
import PuzzleToggle from '../../UI/button/toggle/PuzzleToggle';
import PuzzleButton from '../../UI/button/PuzzleButton';
import { DroppableBox } from './dragAndDrop/DroppableBox';
import { DraggableItem } from './dragAndDrop/DraggableItem';
import _ from 'lodash';
import PuzzlePanel from '../../layout/panel/PuzzlePanel';
import Container from '../../layout/container/Container';
import classNames from 'classnames';

/**
 * Filter container component
 * Renders and saves the filters
 * It also supports the Drag&Drop reordering
 */
class ConditionalFilter extends Component<CONDITIONAL_FILTER_PROPS, CONDITIONAL_FILTER_FILTERS_STATE> {
  private filters: CONDITIONAL_FILTER_FILTERS[] = [];

  constructor(props: CONDITIONAL_FILTER_PROPS) {
    super(props);

    this.state = {
      ...this.state,
      ...props,
      filters: props.value?.length ? props.value : props.filters,
      dropIndex: null,
      dragging: false
    };

    // create filters clone, this is used for updating state because
    // of fast state updates and state async nature
    // this property is updated and then used to update state
    this.filters = _.clone(this.state.filters);
  }

  componentDidUpdate(previousProps) {
    this.flagFilterRow();

    if (this.props.onChange && previousProps.value !== this.props.value) {
      this.setState({
        filters: this.props.value
      });
    }
  }

  componentDidMount() {
    this.flagFilterRow();
  }

  /**
   * This method find all the rows that are nested(withAnd) and flags them for CSS needs
   **/
  flagFilterRow = () => {
    const switchWrap = document.querySelectorAll('.tp-filter-switcher-wrap');

    switchWrap.forEach((el) => {
      const switcherPreviousEl = el.previousSibling as HTMLDivElement;

      if (el.classList.contains('tp-and-state')) {
        switcherPreviousEl.classList.add('tp-and-comparator');
        if (el.classList.contains('tp-dragging')) {
          switcherPreviousEl.classList.add('tp-dragging');
        } else {
          switcherPreviousEl.classList.remove('tp-dragging');
        }
      } else {
        switcherPreviousEl.classList.remove('tp-and-comparator');
      }
    });
  }

  /**
   * Toggleable condition component
   * It is used to add And/Or condition at specified index
   * @param {number} index
   */
  andOrCondition = index => (
    <PuzzleToggle
      cssClass={'k-justify-content-center'}
      setActiveTab={() => {
        let filters = this.state.filters;
        filters[index].value = !filters[index].value;
        this.setState({ filters });
      }}
      checked={this.state.filters[index].value}
      switcherOptions={[
        {
          locale: this.translate('conditionOperators.and'),
          name: 'and'
        },
        {
          locale: this.translate('conditionOperators.or'),
          name: 'or'
        }
      ]}
    />
  );

  /**
   * Add new filter button
   */
  addFilterBtn = () => (
    <Container
      className={'tp-conditional-add'}
      displayFlex
      justifyContentCenter
    >
      <PuzzleButton
        themeColor={'primary'}
        fillMode={'flat'}
        onClick={this.addFilterBtnHandler.bind(this)}
      >
        {this.translate('addAnotherRule')}
      </PuzzleButton>
    </Container>
  )

  /**
   * Add filter button handler
   * It adds new filter config into filters array
   */
  addFilterBtnHandler = () => {
    let filters = this.state.filters;
    let items = [{
      type: 'condition',
      value: false
    }, {
      type: 'filter',
      name: '',
      value: ''
    }];

    if (this.state.filters.length > 1) {
      filters.splice(filters.length - 1, 0, ...items);
    } else {
      filters.splice(filters.length - 1, 0, {
        type: 'filter',
        name: '',
        value: ''
      });
    }

    this.updateFilters(filters);
  }

  /**
   * Updates filters array on D&D drop
   * It reorders item based on what item is droped
   * @param {number} index
   */
  handleDrop = (index: string) => {
    let dropIndex = this.state.dropIndex;
    if (dropIndex !== undefined && dropIndex !== null && dropIndex !== index) {
      let filters = this.state.filters.map(item => Object.assign({}, item));
      let replacerOne = _.clone(filters[index]);
      replacerOne && (replacerOne.filterId = `conditional-field-${dropIndex}`);
      let replacerTwo = _.clone(filters[dropIndex]);
      replacerTwo && (replacerTwo.filterId = `conditional-field-${index}`);

      filters.splice(index, 1, replacerTwo);
      filters.splice(dropIndex, 1, replacerOne);

      this.setState({
        filters: []
      }, () => {
        this.updateFilters(filters);
      });
    }
  }
  /**
   * Toggle drag state
   */
  handleDragging = (dragging: boolean) => {
    this.setState({ dragging });
  }

  /**
   * Saves the dropped item index when the drag starts
   * This index is used to switch places with the drop item
   * @param {number} index
   */
  setDropIndex = (index: string) => {
    this.setState({
      dropIndex: index
    });
  }

  /**
   * Returns filter component
   * @param {PUZZLE_FIELD_PROPS} field Field props
   * @param {number} index
   */
  renderField = (field, index) => (
    <div
      id={`filter-field-${index}`}
      className={classNames('tp-conditional-filter-row', {
        'tp-filter-switcher-wrap': field.type === 'condition',
        'tp-and-state': field.type === 'condition' && !this.state.filters[index].value,
        'tp-dragging': field.type === 'condition' && this.state.dragging
      })}
      key={`filter-field-${index}`}
    >
      {(() => {
        if (field) {
          switch (field.type) {
            case 'condition':
              return this.andOrCondition(index);
            case 'add':
              return this.addFilterBtn();
            default:
              return (
                <DroppableBox
                  id={`dropable-id-${index}`}
                  index={index}
                  onDrop={this.handleDrop}
                  handleDragging={this.handleDragging}
                >
                  <DraggableItem
                    index={index}
                    setDragging={this.handleDragging}
                    data={this.state.data}
                    fieldData={this.state.filters[index]}
                    onFilterChange={this.onFilterChange}
                    removeField={this.removeField.bind(this, index)}
                    setDropIndex={this.setDropIndex}
                  />
                </DroppableBox>
              );
          }
        }
      })()}
    </div>
  )

  /**
   * Updates the filters after filter is changed
   * @param {number} index
   * @param {any} value
   */
  onFilterChange = (index, value) => {
    let filters = this.state.filters;
    filters[index] = {
      ...filters[index],
      ...value
    };

    this.updateFilters(filters);
  }

  /**
   * Removes item from filters and also removes corresponding And/Or condition item
   * @param {number} index
   * @param {Event} e
   */
  removeField = (index, e) => {
    if (this.filters.length <= 2) return;

    let prevIndex = index - 1;
    this.filters.splice(index, 1);

    if (!this.filters[prevIndex]) {
      let nextItem = this.filters[index];
      if (nextItem && nextItem.type !== 'add') {
        this.filters.splice(index, 1);
      }
    } else {
      this.filters.splice(prevIndex, 1);
    }

    this.setState({ filters: [] }, () => {
      this.setState({ filters: _.clone(this.filters) });
    });
  }

  /**
   * Updates state with filters
   * @param {FIELD_STATE[]} filters
   */
  updateFilters = (filters) => {
    this.filters = _.clone(filters);
    if (this.props.onChange) {
      const event = new CustomEvent('filtersEvent', {
        detail: {
          name: this.props.name,
          value: filters
        },
        bubbles: true,
        cancelable: true
      });
      this.props.onChange(event);
    } else {
      this.setState({ filters });
    }
  }

  /**
   * Returns filters
   */
  getValue = () => this.state.filters

  renderBody() {
    const {
      title,
      titleLine,
      titleClassName
    } = this.props;
    return (
      <PuzzlePanel
        titleLine={titleLine}
        title={title ? title : 'Filters'}
        titleClassName={titleClassName}
        className={'tp-conditional-filter'}
      >
        <DragAndDrop>
          {this.state.filters.map(this.renderField)}
        </DragAndDrop>
      </PuzzlePanel>
    );
  }
}

export default ConditionalFilter;
